import React, { useCallback, useEffect, useRef, useState } from "react";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import FileUpload from "component/auth/onboarding/FileUpload";
import EmailSuggestionButton from "component/auth/onboarding/EmailSuggestionButton";
import OnboardingLayout from "component/auth/OnboardingLayout";
import Dropdown from "component/form/dropDown";
import SampleDocumentModal from "component/auth/onboarding/SampleDocumentModal";
import { proofOfAddressSampleDocuments as sampleDocuments } from "component/auth/onboarding/onboardingSampleDocuments";
import Input from "component/form/Input";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowIcon, CheckCircleIcon, SearchIcon } from "asset/icons";
import { Country } from "country-state-city";
import PhoneVerificationModal from "./PhoneVerificationModal";
import ErrorMessage from "../ErrorMessage";
import Loader from "component/loader";
import { createPortal } from "react-dom";
import {
    handleCheckPhoneVerification,
    handleFetchBusinessById,
    handleFetchKybCountries,
    handleUpdateBusinessAddress,
    handleUploadOnboardingDocument,
} from "api/onboarding";
import { cn } from "../../../lib/utils";
import { fetchUserProfile } from "api/fetchers";
const steps = [
    { title: "Business information", subtitles: ["Business details", "Contact details"] },
    { title: "Verify your business" },
    { title: "Add directors" },
];

export const PhoneCountryDropdown = ({ value, options, onChange, loading, disabled = false }) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [menuStyle, setMenuStyle] = useState({});
    const wrapperRef = useRef(null);
    const menuRef = useRef(null);
    const selectedCountry = options.find((option) => option.code === value);
    const filteredOptions = options.filter((option) => {
        const query = search.trim().toLowerCase();
        return (
            option.name.toLowerCase().includes(query) ||
            option.code.toLowerCase().includes(query) ||
            option.dialCode.includes(query)
        );
    });

    const updateMenuPosition = useCallback(() => {
        if (!wrapperRef.current) return;

        const rect = wrapperRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;
        const viewportPadding = 16;
        const menuGap = 8;
        const menuWidth = Math.min(Math.max(rect.width, 300), viewportWidth - viewportPadding * 2);
        const left = Math.min(
            Math.max(rect.left, viewportPadding),
            viewportWidth - menuWidth - viewportPadding
        );
        const desiredHeight = Math.min(Math.max(filteredOptions.length * 48 + 84, 180), 360);
        const spaceBelow = viewportHeight - rect.bottom - viewportPadding - menuGap;
        const spaceAbove = rect.top - viewportPadding - menuGap;
        const shouldOpenUpward = spaceBelow < desiredHeight && spaceAbove > spaceBelow;
        const availableHeight = Math.max(shouldOpenUpward ? spaceAbove : spaceBelow, 120);
        const maxHeight = Math.min(desiredHeight, availableHeight);

        setMenuStyle({
            left,
            maxHeight,
            top: shouldOpenUpward
                ? Math.max(viewportPadding, rect.top - menuGap - maxHeight)
                : Math.min(rect.bottom + menuGap, viewportHeight - viewportPadding),
            width: menuWidth,
        });
    }, [filteredOptions.length]);

    useEffect(() => {
        const handleOutsideClick = (event) => {
            const clickedTrigger = wrapperRef.current?.contains(event.target);
            const clickedMenu = menuRef.current?.contains(event.target);

            if (!clickedTrigger && !clickedMenu) setOpen(false);
        };

        document.addEventListener("mousedown", handleOutsideClick);
        return () => document.removeEventListener("mousedown", handleOutsideClick);
    }, []);

    useEffect(() => {
        if (!open) return;

        updateMenuPosition();
        window.addEventListener("resize", updateMenuPosition);
        window.addEventListener("scroll", updateMenuPosition, true);

        return () => {
            window.removeEventListener("resize", updateMenuPosition);
            window.removeEventListener("scroll", updateMenuPosition, true);
        };
    }, [open, updateMenuPosition]);

    useEffect(() => {
        if (disabled) setOpen(false);
    }, [disabled]);

    const menu =
        open &&
        createPortal(
            <div
                ref={menuRef}
                style={menuStyle}
                className="fixed z-[10000] overflow-hidden rounded-xl border border-[#E6E8F9] bg-white p-3 shadow-[-16px_-16px_16px_-4px_#10182808,16px_16px_32px_-4px_#10182814]"
            >
                <div className="flex h-10 items-center gap-3 rounded-lg bg-[#F4F2FC] px-4">
                    <SearchIcon className="size-5 *:stroke-[#6F739B]" />
                    <input
                        autoFocus
                        type="search"
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder="Search"
                        className="aku-body-normal-regular h-full w-full bg-transparent text-[#101828] outline-none placeholder:text-[#6F739B]"
                    />
                </div>

                <div
                    className="mt-3 overflow-y-auto pr-1"
                    style={{ maxHeight: Math.max((menuStyle.maxHeight || 180) - 64, 96) }}
                >
                    {filteredOptions.map((country) => {
                        const active = country.code === value;

                        return (
                            <button
                                key={country.code}
                                type="button"
                                onClick={() => {
                                    onChange(country.code);
                                    setSearch("");
                                    setOpen(false);
                                }}
                                className={`aku-body-normal-medium flex h-12 w-full items-center gap-3 rounded-lg px-2 text-left text-[#101828] transition-colors ${
                                    active ? "bg-[#F4F2FC]" : "hover:bg-[#F8F7FF]"
                                }`}
                            >
                                <img
                                    src={country.imageUrl}
                                    alt={`${country.name} flag`}
                                    className="size-5 rounded-full object-cover"
                                />
                                <span className="min-w-0 flex-1 truncate">{country.name}</span>
                                <span className="text-sm text-[#6F739B]">{country.dialCode}</span>
                                {active && <CheckCircleIcon className="size-5 shrink-0" />}
                            </button>
                        );
                    })}
                </div>
            </div>,
            document.body
        );

    return (
        <div ref={wrapperRef} className="h-full">
            <button
                type="button"
                disabled={loading || disabled}
                onClick={() => !loading && !disabled && setOpen((prev) => !prev)}
                className="aku-body-normal-regular flex h-full min-w-[138px] items-center justify-between gap-3 rounded-lg bg-[#f1f5f9] px-4 text-[#101828] disabled:cursor-not-allowed disabled:opacity-70"
            >
                <span className="flex items-center gap-2">
                    {selectedCountry?.imageUrl && (
                        <img
                            src={selectedCountry.imageUrl}
                            alt={`${selectedCountry.name} flag`}
                            className="size-5 rounded-full object-cover"
                        />
                    )}
                    <span>{selectedCountry?.code || "--"}</span>
                </span>
                <ArrowIcon className="size-3.5 shrink-0 *:stroke-[#6F739B]" />
            </button>
            {menu}
        </div>
    );
};

export const getUploadUrl = (raw) =>
    (typeof raw === "string" && raw) ||
    raw?.link ||
    raw?.data?.link ||
    raw?.url?.link ||
    raw?.data?.url?.link ||
    raw?.url ||
    raw?.fileUrl ||
    raw?.secure_url ||
    raw?.Location ||
    raw?.location ||
    raw?.data?.url ||
    raw?.data?.fileUrl ||
    raw?.data?.secure_url ||
    raw?.data?.Location ||
    raw?.data?.location ||
    "";

export const getUploadKey = (raw, fallback) =>
    raw?.key ||
    raw?.fileKey ||
    raw?.name ||
    raw?.Key ||
    raw?.data?.key ||
    raw?.data?.fileKey ||
    raw?.data?.name ||
    raw?.data?.Key ||
    fallback;

export const getRegisteredBusinessCountryCode = (business = {}) =>
    String(
        business?.businessCountry?.code ||
            business?.data?.businessCountry?.code ||
            business?.countryCode ||
            business?.data?.countryCode ||
            ""
    ).toUpperCase();

export const getCountryCodeFromName = (countryName) => {
    const normalizedName = String(countryName || "").trim().toLowerCase();
    if (!normalizedName) return "";

    return (
        Country.getAllCountries().find(
            (country) => country.name.toLowerCase() === normalizedName
        )?.isoCode || ""
    );
};

const normalizeCountryName = (countryName) => String(countryName || "").trim().toLowerCase();
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ContactDetails = ({ currentStep = 0, isUpdating }) => {
    const [formData, setFormData] = useState({
        phoneNumber: "",
        businessEmail: "",
        businessAddress: {
            houseNumber: "",
            street: "",
            city: "",
            state: "",
            zipCode: "",
            subdivision: "",
        },
        physicalAddress: {
            houseNumber: "",
            street: "",
            city: "",
            state: "",
            zipCode: "",
            subdivision: "",
        },
        sameAsBusinessAddress: false,
        documentType: "",
    });

    const [proofOfAddress, setProofOfAddress] = useState(null);
    const [uploadError, setUploadError] = useState("");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [activeTab, setActiveTab] = useState("Utility bill");
    const [showModal, setShowModal] = useState(false);
    const [formError, setFormError] = useState("");
    const [submittedPhone, setSubmittedPhone] = useState("");
    const [, setPhoneError] = useState("");
    const [, setZipError] = useState("");
    const [validationErrors, setValidationErrors] = useState({});
    const [phoneCountryCode, setPhoneCountryCode] = useState("");
    const [phoneCountryOptions, setPhoneCountryOptions] = useState([]);
    const [countriesLoading, setCountriesLoading] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const shouldReturnToSummary =
        new URLSearchParams(location.search).get("returnTo") === "summary";
    const [loading, setLoading] = useState(false);
    const currentBusiness = useSelector((state) => state);
    const businessId = currentBusiness.businessid || currentBusiness.business?._id;
    const accountEmail =
        currentBusiness.users?.email || currentBusiness.user?.email || "";
    const businessCountry = currentBusiness.business?.businessCountry;
    const country = businessCountry?.name || currentBusiness.business?.country;
    const countryCode = businessCountry?.code || currentBusiness.business?.countryCode;
    const normalizedCountryCode = String(
        countryCode || getCountryCodeFromName(country)
    ).toUpperCase();
    const [registeredCountryCode, setRegisteredCountryCode] = useState(normalizedCountryCode);
    const [registeredCountryName, setRegisteredCountryName] = useState(country || "");
    const [phoneCountryTouched, setPhoneCountryTouched] = useState(false);
    const [isEditingPhone, setIsEditingPhone] = useState(false);
    const verifiedRef = useRef(false);
    const profilePhoneHydratedRef = useRef(false);
    const [existingProofName, setExistingProofName] = useState("");
    const { data: userProfile, isLoading: isLoadingUserProfile } = useQuery({
        queryKey: ["fetchUserProfile"],
        queryFn: fetchUserProfile,
    });
    const profilePhoneNumber = String(userProfile?.phoneNumber || "").replace(/[^\d+]/g, "");
    const isPhoneLocked = Boolean(profilePhoneNumber) && !isEditingPhone;

    const documentOptionsMap = {
        Nigeria: [
            { label: "Utility bill", value: "Utility bill" },
            { label: "Tenancy agreement", value: "Tenancy agreement" },
        ],
        "United States": [
            { label: "Recent utility bill", value: "Recent utility bill" },
        ],
        "United Kingdom": [
            { label: "Recent utility bill", value: "Recent utility bill" },
            { label: "Council tax bill", value: "Council tax bill" },
            { label: "Lease agreement", value: "Lease agreement" },
        ],
    };

    const getPhoneDialCode = useCallback(
        (code) => phoneCountryOptions.find((option) => option.code === code)?.dialCode || "",
        [phoneCountryOptions]
    );

    const normalizeCountriesResponse = (responseBody) =>
        (Array.isArray(responseBody?.data) && responseBody.data) ||
        (Array.isArray(responseBody?.data?.countries) && responseBody.data.countries) ||
        (Array.isArray(responseBody?.countries) && responseBody.countries) ||
        (Array.isArray(responseBody) && responseBody) ||
        [];

    useEffect(() => {
        if (normalizedCountryCode) {
            setRegisteredCountryCode(normalizedCountryCode);
        }
        if (country) {
            setRegisteredCountryName(country);
        }
    }, [country, normalizedCountryCode]);

    useEffect(() => {
        if (normalizedCountryCode || !businessId) return;

        let ignore = false;

        const fetchRegisteredCountry = async () => {
            try {
                const response = await handleFetchBusinessById(businessId);
                const business = response?.business || response;
                const fetchedCountryName =
                    business?.businessCountry?.name ||
                    business?.data?.businessCountry?.name ||
                    business?.country ||
                    business?.data?.country ||
                    "";
                const fetchedCountryCode =
                    getRegisteredBusinessCountryCode(business) ||
                    getCountryCodeFromName(fetchedCountryName);

                if (!ignore) {
                    if (fetchedCountryName) setRegisteredCountryName(fetchedCountryName);
                    if (fetchedCountryCode) setRegisteredCountryCode(fetchedCountryCode);
                }
            } catch (_) {}
        };

        fetchRegisteredCountry();

        return () => {
            ignore = true;
        };
    }, [businessId, normalizedCountryCode]);

    useEffect(() => {
        let ignore = false;

        const fetchCountries = async () => {
            setCountriesLoading(true);
            try {
                const responseBody = await handleFetchKybCountries();
                const countries = normalizeCountriesResponse(responseBody);
                const options = countries
                    .map((item) => {
                        const isoCode = String(item.code || "").toUpperCase();
                        const countryMeta = Country.getCountryByCode(isoCode);
                        const phonecode =
                            item.phoneCode ||
                            item.phonecode ||
                            item.phone?.[0] ||
                            countryMeta?.phonecode;

                        if (!isoCode || !phonecode) return null;

                        return {
                            name: item.name,
                            code: isoCode,
                            imageUrl: item.imageUrl,
                            dialCode: String(phonecode).startsWith("+")
                                ? String(phonecode)
                                : `+${phonecode}`,
                        };
                    })
                    .filter(Boolean);

                if (!ignore) {
                    setPhoneCountryOptions(options);
                }
            } catch (error) {
                if (!ignore) {
                    setFormError(
                        error?.message || "Unable to load phone countries. Please refresh and try again."
                    );
                }
            } finally {
                if (!ignore) setCountriesLoading(false);
            }
        };

        fetchCountries();

        return () => {
            ignore = true;
        };
    }, []);

    useEffect(() => {
        if (shouldReturnToSummary || phoneCountryTouched || !phoneCountryOptions.length) return;
        if (!registeredCountryCode && !registeredCountryName) return;

        const matchingCountry = phoneCountryOptions.find(
            (option) =>
                option.code === registeredCountryCode ||
                normalizeCountryName(option.name) === normalizeCountryName(registeredCountryName)
        );

        if (matchingCountry) {
            setPhoneCountryCode(matchingCountry.code);
        }
    }, [
        phoneCountryOptions,
        phoneCountryTouched,
        registeredCountryCode,
        registeredCountryName,
        shouldReturnToSummary,
    ]);

    useEffect(() => {
        if (
            profilePhoneHydratedRef.current ||
            !profilePhoneNumber ||
            !phoneCountryOptions.length
        ) {
            return;
        }

        const matchingCountry = [...phoneCountryOptions]
            .sort((a, b) => b.dialCode.length - a.dialCode.length)
            .find((option) => profilePhoneNumber.startsWith(option.dialCode));
        const selectedCountryCode = matchingCountry?.code || registeredCountryCode;
        const dialCode = matchingCountry?.dialCode || getPhoneDialCode(selectedCountryCode);
        const localPhoneNumber = dialCode
            ? profilePhoneNumber.slice(dialCode.length)
            : profilePhoneNumber.replace(/^\+/, "");

        if (selectedCountryCode) setPhoneCountryCode(selectedCountryCode);
        setFormData((current) => ({
            ...current,
            phoneNumber: localPhoneNumber,
        }));
        profilePhoneHydratedRef.current = true;
    }, [
        getPhoneDialCode,
        phoneCountryOptions,
        profilePhoneNumber,
        registeredCountryCode,
    ]);

    useEffect(() => {
        if (!shouldReturnToSummary || !businessId) return;
        let ignore = false;

        const prefillContactDetails = async () => {
            try {
                const response = await handleFetchBusinessById(businessId);
                const business = response?.business || response;
                if (ignore || !business) return;

                const registered = business?.businessAddress || {};
                const physical = business?.physicalAddress || registered;
                const proof = business?.proofOfAddress || {};
                const addressesMatch = ["houseNumber", "street", "city", "state", "zipCode", "subdivision"]
                    .every((key) => String(registered?.[key] || "") === String(physical?.[key] || ""));

                setFormData((prev) => ({
                    ...prev,
                    businessEmail: business?.email || business?.businessEmail || "",
                    businessAddress: { ...prev.businessAddress, ...registered },
                    physicalAddress: { ...prev.physicalAddress, ...physical },
                    sameAsBusinessAddress: addressesMatch,
                    documentType: proof?.type || proof?.documentType || proof?.name || "",
                }));
                setProofOfAddress(proof);
                setExistingProofName(getUploadKey(proof, proof?.type || "Proof of address"));
            } catch (error) {
                if (!ignore) setFormError(error?.message || "Unable to load contact details.");
            }
        };
        prefillContactDetails();
        return () => { ignore = true; };
    }, [businessId, shouldReturnToSummary]);

    useEffect(() => {
        if (!shouldReturnToSummary || profilePhoneNumber || !phoneCountryOptions.length) return;
        const prefillPhone = async () => {
            try {
                const response = await handleFetchBusinessById(businessId);
                const business = response?.business || response;
                const fullPhone = String(business?.phoneNumber || "").replace(/\s/g, "");
                if (!fullPhone) return;
                const matchingCountry = [...phoneCountryOptions]
                    .sort((a, b) => b.dialCode.length - a.dialCode.length)
                    .find((option) => fullPhone.startsWith(option.dialCode));
                const selectedCode = matchingCountry?.code || countryCode || phoneCountryCode;
                const dialCode = matchingCountry?.dialCode || getPhoneDialCode(selectedCode);
                setPhoneCountryCode(selectedCode);
                setFormData((prev) => ({
                    ...prev,
                    phoneNumber: fullPhone.replace(dialCode, "").replace(/^\+/, ""),
                }));
            } catch (_) {}
        };
        prefillPhone();
    }, [
        businessId,
        countryCode,
        getPhoneDialCode,
        phoneCountryCode,
        phoneCountryOptions,
        profilePhoneNumber,
        shouldReturnToSummary,
    ]);

    const documentOptions = documentOptionsMap[country] || [
        { label: "Utility bill", value: "Utility bill" },
        { label: "Lease agreement", value: "Lease agreement" },
    ];

    const phoneValidationRules = {
        Nigeria: {
            regex: /^\d{10}$/,
            message: "Phone number must be 10 digits without country code.",
        },
        "United States": {
            regex: /^[2-9]\d{2}[2-9]\d{2}\d{4}$/,
            message: "Enter a valid 10-digit US phone number without country code.",
        },
        "United Kingdom": {
            regex: /^7\d{9}$/,
            message: "Enter a valid 10-digit UK mobile number (starting with 7).",
        },
    };

    const zipValidationRules = {
        Nigeria: {
            regex: /^\d{6}$/,
            message: "ZIP code must be 6 digits.",
        },
        "United States": {
            regex: /^\d{5}(-\d{4})?$/,
            message: "Enter a valid US ZIP code (5 or ZIP+4).",
        },
        "United Kingdom": {
            regex: /^[A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2}$/i,
            message: "Enter a valid UK postcode (e.g. SW1A 1AA).",
        },
    };

    const validatePhoneNumber = (phone, country) => {
        const rule = phoneValidationRules[country];
        if (!rule) return /^\d{6,15}$/.test(phone);

        return rule.regex.test(phone);
    };

    const selectedPhoneCountryName =
        phoneCountryOptions.find((option) => option.code === phoneCountryCode)?.name || country;
    const effectivePhysicalAddress = formData.sameAsBusinessAddress
        ? formData.businessAddress
        : formData.physicalAddress;
    const hasValue = (value) => Boolean(String(value ?? "").trim());
    const isZipCodeValid = (value) => {
        const rule = zipValidationRules[country];
        return !rule || rule.regex.test(String(value ?? "").trim());
    };
    const hasUploadedProof = Boolean(getUploadUrl(proofOfAddress));
    const businessEmailIsValid = EMAIL_PATTERN.test(formData.businessEmail.trim());

    const isFormValid =
        !!phoneCountryCode &&
        validatePhoneNumber(formData.phoneNumber, selectedPhoneCountryName) &&
        businessEmailIsValid &&
        hasValue(formData.businessAddress.houseNumber) &&
        hasValue(formData.businessAddress.street) &&
        hasValue(formData.businessAddress.city) &&
        hasValue(formData.businessAddress.state) &&
        hasValue(formData.businessAddress.zipCode) &&
        isZipCodeValid(formData.businessAddress.zipCode) &&
        hasValue(effectivePhysicalAddress.houseNumber) &&
        hasValue(effectivePhysicalAddress.street) &&
        hasValue(effectivePhysicalAddress.city) &&
        hasValue(effectivePhysicalAddress.state) &&
        hasValue(effectivePhysicalAddress.zipCode) &&
        isZipCodeValid(effectivePhysicalAddress.zipCode) &&
        !!formData.documentType &&
        hasUploadedProof;

    const handleChange = (field, value) => {
        setValidationErrors((current) => {
            if (!current[field]) return current;
            const next = { ...current };
            delete next[field];
            return next;
        });
        setFormData((prev) => ({ ...prev, [field]: value }));

        if (field === "phoneNumber") {
            if (!validatePhoneNumber(value, country)) {
                setPhoneError(
                    phoneValidationRules[country]?.message ||
                        "Enter a valid phone number without country code."
                );
            } else {
                setPhoneError("");
            }
        }

        if (field === "businessZipCode" || field === "physicalZipCode") {
            const rule = zipValidationRules[country];
            if (rule && !rule.regex.test(value)) {
                setZipError(rule.message);
            } else {
                setZipError("");
            }
        }
    };

    const validateZipCode = (value) => {
        const rule = zipValidationRules[country];
        if (rule && !rule.regex.test(value)) {
            setZipError(rule.message);
        } else {
            setZipError("");
        }
    };

    const handleAddressChange = (addressKey, field, value) => {
        const validationKey = `${addressKey}.${field}`;
        setValidationErrors((current) => {
            if (!current[validationKey]) return current;
            const next = { ...current };
            delete next[validationKey];
            return next;
        });
        setFormData((prev) => {
            const nextAddress = {
                ...prev[addressKey],
                [field]: value,
            };
            const nextFormData = {
                ...prev,
                [addressKey]: nextAddress,
            };

            if (addressKey === "businessAddress" && prev.sameAsBusinessAddress) {
                nextFormData.physicalAddress = nextAddress;
            }

            return nextFormData;
        });

        if (field === "zipCode") {
            validateZipCode(value);
        }
    };

    const handleSameAsBusinessAddressChange = (checked) => {
        setFormData((prev) => ({
            ...prev,
            sameAsBusinessAddress: checked,
            physicalAddress: checked ? prev.businessAddress : prev.physicalAddress,
        }));

        validateZipCode(
            checked ? formData.businessAddress.zipCode : formData.physicalAddress.zipCode
        );
    };

    const handleFileUpload = async (file) => {
        if (!file) return;

        if (!formData.documentType) {
            setUploadError("Please select a document type before uploading.");
            return;
        }

        try {
            setUploadError("");
            setValidationErrors((current) => {
                if (!current.proofOfAddress) return current;
                const next = { ...current };
                delete next.proofOfAddress;
                return next;
            });

            const response = await handleUploadOnboardingDocument(file);
            const uploadData = response?.data || response;
            const url = getUploadUrl(uploadData);

            if (!url) {
                throw new Error(response?.message || "No upload URL returned");
            }

            setProofOfAddress({
                ...((typeof uploadData === "object" && uploadData) || {}),
                url,
                key: getUploadKey(uploadData, file.name),
            });
        } catch (error) {
            console.error("Upload failed:", error);
            setProofOfAddress(null);
            setUploadError(error?.message || "Failed to upload document. Please try again.");
        }
    };

    const navigateToNextStep = () => {
        if (shouldReturnToSummary) {
            navigate("/onboarding/summary");
            return;
        }

        const type = currentBusiness?.business?.type;
        if (type === "LLC") {
            navigate("/onboarding/business-verification/compliance/llc");
        } else if (type === "SoleProprietorship") {
            navigate("/onboarding/business-verification/compliance/soleproprietorship");
        }
    };

    const isPhoneVerificationSupported = async () => {
        if (!phoneCountryCode) return false;

        const responseBody = await handleCheckPhoneVerification(phoneCountryCode);
        return Boolean(responseBody?.canVerify ?? responseBody?.data?.canVerify);
    };

    const buildProofOfAddressPayload = () => {
        const uploadData = proofOfAddress || {};
        const url = getUploadUrl(uploadData) || getUploadUrl(uploadData?.data);
        const key = getUploadKey(uploadData, getUploadKey(uploadData?.data, formData.documentType));

        return {
            url,
            key,
            type: formData.documentType,
        };
    };

    const handleSubmit = async () => {
        if (!businessId) return;

        setLoading(true);
        try {
            const dialCode = getPhoneDialCode(phoneCountryCode);
            const fullPhoneNumber = `${dialCode}${formData.phoneNumber.trim()}`;
            const proofOfAddressPayload = buildProofOfAddressPayload();

            if (!proofOfAddressPayload.url) {
                throw new Error("Please upload a valid proof of address document.");
            }

            const payload = {
                phoneNumber: fullPhoneNumber,
                email: formData.businessEmail.trim(),
                businessAddress: formData.businessAddress,
                physicalAddress: formData.physicalAddress,
                proofOfAddress: proofOfAddressPayload,
            };

            const updateResponse = await handleUpdateBusinessAddress(businessId, payload);
            if (updateResponse?.success === false || updateResponse?.status === false) {
                throw new Error(
                    updateResponse?.message || "Unable to update contact details. Please try again."
                );
            }

            setSubmittedPhone(fullPhoneNumber);
            setFormError("");

            if (await isPhoneVerificationSupported()) {
                setShowModal(true);
            } else {
                navigateToNextStep();
            }
        } catch (error) {
            console.error("Failed to update contact details:", error);
            const message = error?.message || "Something went wrong. Please try again.";
            setFormError(message);
        } finally {
            setLoading(false);
        }
    };

    const handleInvalidSubmit = () => {
        if (loading) return;

        const requiredErrors = {};
        if (!phoneCountryCode) requiredErrors.phoneNumber = "This field is required.";
        if (!formData.phoneNumber) requiredErrors.phoneNumber = "This field is required.";
        else if (!validatePhoneNumber(formData.phoneNumber, selectedPhoneCountryName)) {
            requiredErrors.phoneNumber =
                phoneValidationRules[selectedPhoneCountryName]?.message ||
                "Enter a valid phone number without country code.";
        }
        if (!formData.businessEmail.trim()) {
            requiredErrors.businessEmail = "This field is required.";
        } else if (!businessEmailIsValid) {
            requiredErrors.businessEmail = "Enter a valid business email address.";
        }
        ["houseNumber", "street", "state", "city", "zipCode"].forEach((field) => {
            if (!hasValue(formData.businessAddress[field])) {
                requiredErrors[`businessAddress.${field}`] = "This field is required.";
            }
        });
        if (!formData.sameAsBusinessAddress) {
            ["houseNumber", "street", "state", "city", "zipCode"].forEach((field) => {
                if (!hasValue(formData.physicalAddress[field])) {
                    requiredErrors[`physicalAddress.${field}`] = "This field is required.";
                }
            });
        }
        if (
            hasValue(formData.businessAddress.zipCode) &&
            !isZipCodeValid(formData.businessAddress.zipCode)
        ) {
            requiredErrors["businessAddress.zipCode"] =
                zipValidationRules[country]?.message || "Enter a valid ZIP/postal code.";
        }
        if (
            !formData.sameAsBusinessAddress &&
            hasValue(formData.physicalAddress.zipCode) &&
            !isZipCodeValid(formData.physicalAddress.zipCode)
        ) {
            requiredErrors["physicalAddress.zipCode"] =
                zipValidationRules[country]?.message || "Enter a valid ZIP/postal code.";
        }
        if (!formData.documentType) requiredErrors.documentType = "This field is required.";
        if (!hasUploadedProof) requiredErrors.proofOfAddress = "This field is required.";

        setValidationErrors(requiredErrors);
        const firstInvalidField = Object.keys(requiredErrors)[0];
        requestAnimationFrame(() => {
            const field = document.querySelector(
                `[data-onboarding-field="${firstInvalidField}"]`
            );
            field?.scrollIntoView({ behavior: "smooth", block: "center" });
            field?.querySelector("input, textarea, button")?.focus({ preventScroll: true });
        });
    };

    return (
        <OnboardingLayout
            title="Activate account"
            steps={steps}
            currentStep={currentStep}
            currentSubStep={1}
        onSkip={() => console.log("Skip clicked")}
        isUpdating={isUpdating}
        >
        <div className={cn("max-w-[1200px] relative justify-center", !isUpdating ? "md:pl-96" : "")}>
          <form className="pb-8">
                    <h3 className="aku-heading-h2-medium text-[#101828]">Where can we reach you?</h3>

                    <div className="mt-8 space-y-4">
                    {formError && <ErrorMessage message={formError} />}

                    <div data-onboarding-field="phoneNumber">
                        <label className="aku-body-small-medium block text-[#344054] mb-2">
                            Company phone number
                        </label>
                        <div className="flex h-[56px] w-full items-center gap-2 rounded-lg">
                            <PhoneCountryDropdown
                                value={phoneCountryCode}
                                options={phoneCountryOptions}
                                onChange={(value) => {
                                    setPhoneCountryTouched(true);
                                    setPhoneCountryCode(value);
                                    setPhoneError("");
                                }}
                                loading={countriesLoading}
                                disabled={isLoadingUserProfile || isPhoneLocked}
                            />
                            <div className="relative flex-1">
                                <span className="pointer-events-none absolute left-5 top-1/2 z-10 -translate-y-1/2 text-[#101828]">
                                    {getPhoneDialCode(phoneCountryCode)}
                                </span>
                                <Input
                                    type="tel"
                                    placeholder={
                                        country === "Nigeria"
                                            ? "905 555 0123"
                                            : country === "United States"
                                            ? "415 555 2671"
                                            : "Enter phone number"
                                    }
                                    maxLength={15}
                                    value={formData.phoneNumber}
                                    onChange={(e) =>
                                        handleChange("phoneNumber", e.replace(/[^\d]/g, ""))
                                    }
                                    className={`!mt-0 !pl-20 ${isPhoneLocked ? "!pr-24" : ""}`}
                                    inputClassName={
                                        isPhoneLocked
                                            ? "!text-[#101828] disabled:[-webkit-text-fill-color:#101828]"
                                            : ""
                                    }
                                    disabled={isLoadingUserProfile || isPhoneLocked}
                                    error={validationErrors.phoneNumber}
                                />
                                {isPhoneLocked && (
                                    <button
                                        type="button"
                                        onClick={() => setIsEditingPhone(true)}
                                        className="aku-body-normal-medium absolute right-5 top-1/2 z-10 -translate-y-1/2 text-[#7000FF]"
                                    >
                                        Change
                                    </button>
                                )}
                            </div>
                        </div>
                        {phoneCountryCode === "NG" && formData.phoneNumber && (
                            <p className="mt-2 text-sm text-[#6F739B]">
                                We'll send an OTP to verify this number
                            </p>
                        )}
                    </div>

                    <div data-onboarding-field="businessEmail">
                        <label className="aku-body-small-medium mb-2 block text-[#344054]">
                            Business email
                        </label>
                        <Input
                            type="email"
                            value={formData.businessEmail}
                            onChange={(value) => handleChange("businessEmail", value)}
                            placeholder="Enter business email address"
                            className="!mt-0"
                            error={validationErrors.businessEmail}
                        />
                        {accountEmail && formData.businessEmail !== accountEmail && (
                            <EmailSuggestionButton
                                email={accountEmail}
                                onClick={() => handleChange("businessEmail", accountEmail)}
                            />
                        )}
                    </div>

                    <div>
                        <label className="aku-body-small-medium block text-[#344054] mb-2">
                            Registered business address
                        </label>
                        <div className="space-y-2">
                            <div data-onboarding-field="businessAddress.houseNumber">
                                <Input
                                    type="text"
                                    value={formData.businessAddress.houseNumber}
                                    onChange={(e) => handleAddressChange("businessAddress", "houseNumber", e)}
                                    placeholder="House number"
                                    className="!mt-0"
                                    error={validationErrors["businessAddress.houseNumber"]}
                                />
                            </div>
                            <div data-onboarding-field="businessAddress.street">
                                <Input
                                    type="text"
                                    value={formData.businessAddress.street}
                                    onChange={(e) => handleAddressChange("businessAddress", "street", e)}
                                    placeholder="Street address"
                                    className="!mt-0"
                                    error={validationErrors["businessAddress.street"]}
                                />
                            </div>

                            <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                                <div data-onboarding-field="businessAddress.state">
                                    <Input
                                        type="text"
                                        value={formData.businessAddress.state}
                                        onChange={(e) => handleAddressChange("businessAddress", "state", e)}
                                        placeholder="State / Province"
                                        className="!mt-0"
                                        error={validationErrors["businessAddress.state"]}
                                    />
                                </div>
                                <div data-onboarding-field="businessAddress.city">
                                    <Input
                                        type="text"
                                        value={formData.businessAddress.city}
                                        onChange={(e) => handleAddressChange("businessAddress", "city", e)}
                                        placeholder="City"
                                        className="!mt-0"
                                        error={validationErrors["businessAddress.city"]}
                                    />
                                </div>

                                <div data-onboarding-field="businessAddress.zipCode">
                                <Input
                                    type={country === "Nigeria" || country === "United States" ? "number" : "text"}
                                    inputMode={
                                        country === "Nigeria" || country === "United States"
                                            ? "numeric"
                                            : "text"
                                    }
                                    placeholder="Postal / ZIP Code"
                                    maxLength={
                                        country === "Nigeria" ? 6 : country === "United States" ? 9 : 8
                                    }
                                    value={formData.businessAddress.zipCode}
                                    onChange={(e) => handleAddressChange("businessAddress", "zipCode", e)}
                                    className="!mt-0"
                                    error={validationErrors["businessAddress.zipCode"]}
                                />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div>
                        <div className="mb-2 flex items-center justify-between gap-4">
                            <label className="aku-body-small-medium block text-[#344054]">
                                Physical address
                            </label>
                            <label className="aku-body-small-medium flex items-center gap-2 text-[#101828]">
                                <input
                                    type="checkbox"
                                    checked={formData.sameAsBusinessAddress}
                                    onChange={(event) =>
                                        handleSameAsBusinessAddressChange(event.target.checked)
                                    }
                                    className="size-4 accent-[#6F00FF]"
                                />
                                Same as business address
                            </label>
                        </div>
                        <div className="space-y-2">
                            <div data-onboarding-field="physicalAddress.houseNumber">
                            <Input
                                type="text"
                                value={formData.physicalAddress.houseNumber}
                                onChange={(e) =>
                                    handleAddressChange("physicalAddress", "houseNumber", e)
                                }
                                placeholder="House number"
                                disabled={formData.sameAsBusinessAddress}
                                className="!mt-0"
                                error={validationErrors["physicalAddress.houseNumber"]}
                            />
                            </div>
                            <div data-onboarding-field="physicalAddress.street">
                            <Input
                                type="text"
                                value={formData.physicalAddress.street}
                                onChange={(e) => handleAddressChange("physicalAddress", "street", e)}
                                placeholder="Street address"
                                disabled={formData.sameAsBusinessAddress}
                                className="!mt-0"
                                error={validationErrors["physicalAddress.street"]}
                            />
                            </div>

                            <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                                <div data-onboarding-field="physicalAddress.state">
                                <Input
                                    type="text"
                                    value={formData.physicalAddress.state}
                                    onChange={(e) => handleAddressChange("physicalAddress", "state", e)}
                                    placeholder="State / Province"
                                    disabled={formData.sameAsBusinessAddress}
                                    className="!mt-0"
                                    error={validationErrors["physicalAddress.state"]}
                                />
                                </div>
                                <div data-onboarding-field="physicalAddress.city">
                                <Input
                                    type="text"
                                    value={formData.physicalAddress.city}
                                    onChange={(e) => handleAddressChange("physicalAddress", "city", e)}
                                    placeholder="City"
                                    disabled={formData.sameAsBusinessAddress}
                                    className="!mt-0"
                                    error={validationErrors["physicalAddress.city"]}
                                />
                                </div>
                                <div data-onboarding-field="physicalAddress.zipCode">
                                <Input
                                    type={
                                        country === "Nigeria" || country === "United States"
                                            ? "number"
                                            : "text"
                                    }
                                    inputMode={
                                        country === "Nigeria" || country === "United States"
                                            ? "numeric"
                                            : "text"
                                    }
                                    placeholder="Postal / ZIP Code"
                                    maxLength={
                                        country === "Nigeria" ? 6 : country === "United States" ? 9 : 8
                                    }
                                    value={formData.physicalAddress.zipCode}
                                    onChange={(e) => handleAddressChange("physicalAddress", "zipCode", e)}
                                    disabled={formData.sameAsBusinessAddress}
                                    className="!mt-0"
                                    error={validationErrors["physicalAddress.zipCode"]}
                                />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-2">
                        <div data-onboarding-field="documentType">
                        <Dropdown
                            value={formData.documentType}
                            label="Upload proof of address"
                            labelClassName="aku-body-small-medium !mb-2"
                            placeholder="Document type"
                            options={documentOptions}
                            onChange={(value) => {
                                handleChange("documentType", value);
                                setActiveTab(value);
                                setProofOfAddress(null);
                                setUploadError("");
                            }}
                            error={validationErrors.documentType}
                        />
                        </div>

                        <div data-onboarding-field="proofOfAddress">
                        <FileUpload
                            label="Drag or Upload file here"
                            initialFileName={existingProofName}
                            onFileSelect={handleFileUpload}
                            accept="image/png, image/jpeg, application/pdf"
                            maxSize={5 * 1024 * 1024}
                            disabled={!formData.documentType}
                            validationError={validationErrors.proofOfAddress}
                        />
                        </div>

                        {uploadError && <p className="text-sm text-red-500">{uploadError}</p>}

                        <div className="text-sm text-[#DC6803] flex items-center gap-2">
                            <span className="text-lg">⚠️</span>
                            <span>
                                Your document should clearly show the physical address provided earlier.
                            </span>
                        </div>
                    </div>

                    <span
                        className="text-[#6F00FF] text-base font-normal flex gap-1 items-center cursor-pointer"
                        onClick={() => setIsModalOpen(true)}
                    >
                        See sample <span className="material-icons">chevron_right</span>
                    </span>
                    </div>

                    <div className="flex gap-4 justify-end pb-5">
                        <button
                            type="button"
                            onClick={isFormValid ? handleSubmit : handleInvalidSubmit}
                            disabled={loading}
                            aria-disabled={!isFormValid || loading}
                            className={`rounded-lg px-6 !py-3 text-sm font-semibold ${
                                isFormValid
                                    ? "bg-[#7F56D9] text-white hover:bg-[#6f00ff]"
                                    : "bg-[#E9D7FE] text-white cursor-not-allowed"
                            }`}
                        >
                            {loading ? <Loader /> : "Continue"}
                        </button>
                    </div>
                </form>
            </div>

            <SampleDocumentModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title="Proof of Address Samples"
                tabs={documentOptions.map((doc) => doc.label)}
                activeTab={activeTab}
                onTabChange={setActiveTab}
                documentSrc={sampleDocuments[activeTab]}
            />

            <PhoneVerificationModal
                phone={submittedPhone}
                isOpen={showModal}
                onClose={() => {
                    if (!verifiedRef.current) {
                        window.location.href = shouldReturnToSummary
                            ? "/onboarding/summary"
                            : "/dashboard";
                    }
                    setShowModal(false);
                }}
                onVerified={() => {
                    verifiedRef.current = true;
                    setShowModal(false);
                    if (shouldReturnToSummary) {
                        navigate("/onboarding/summary");
                        return;
                    }
                    const type = currentBusiness?.business?.type;
                    if (type === "LLC") {
                        navigate("/onboarding/business-verification/compliance/llc");
                    } else if (type === "SoleProprietorship") {
                        navigate("/onboarding/business-verification/compliance/soleproprietorship");
                    }
                }}
            />
        </OnboardingLayout>
    );
};

export default ContactDetails;
